import './state-input.scss';

import { reactiveVueMixin } from '@skinsoft/reactive-vue';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { defineComponent, PropType } from 'vue';

import { Input, ValidationStates } from './input';
export { ValidationStates } from './input';

// Input's untyped `value` computed getter breaks its inferred method types repo-wide; this local shape keeps refs into it precisely typed here without editing that shared file.
interface InputRef {
    clear(): void;
    write(value: string): void;
    select(): void;
    blur(): void;
    value: string;
}

export const StateInput = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sInput: Input,
    },
    props: {
        active: { type: Boolean },
        validator: { type: Function as PropType<(value: string) => Promise<boolean> | boolean> },
    },
    template: require('./state-input.html'),
    data() {
        return {
            ValidationStates,
            validationState: ValidationStates.PENDING as ValidationStates,
            input$: new Subject<string>(),
        }
    },
    mounted() {
        this.subscribeTo(
            this.input$.pipe(
                debounceTime(2000),
                distinctUntilChanged(),
                filter(() => this.active && this.validationState !== ValidationStates.SUCCESS),
            ),
            async value => {
                if (!value || !this.validator) {
                    this.validationState = ValidationStates.PENDING;
                    return;
                }

                this.validationState = ValidationStates.VALIDATING;

                try {
                    this.validationState = (await this.validator(value))
                        ? ValidationStates.SUCCESS
                        : ValidationStates.ERROR;
                } catch (error) {
                    this.validationState = ValidationStates.ERROR;
                }
            },
        );
    },
    methods: {
        reset() {
            this.validationState = ValidationStates.PENDING;
            (this.$refs.input as InstanceType<typeof Input> & InputRef).clear();
        },
        validate(value) {
            this.validationState = ValidationStates.SUCCESS;
            (this.$refs.input as InstanceType<typeof Input> & InputRef).write(value);
        },
        onInputChange(value) {
            this.input$.next(value);
        },
        setValue(value: string) {
            (this.$refs.input as InstanceType<typeof Input> & InputRef).write(value)
        },
        onClick() {
            this.$emit('click');
        },
    },
    watch: {
        validationState: {
            handler(value: ValidationStates) {
                switch (value) {
                    case ValidationStates.ERROR:
                        this.$emit('error');
                        (this.$refs.input as InstanceType<typeof Input> & InputRef).select();
                        break;

                    case ValidationStates.SUCCESS:
                        (this.$refs.input as InstanceType<typeof Input> & InputRef).blur();
                        this.$emit('change', value, (this.$refs.input as InstanceType<typeof Input> & InputRef).value);
                        break;

                    default:
                        break;
                }
            },
            immediate: false,
        },
        active: {
            handler(value) {
                if (
                    !value &&
                    (this.validationState === ValidationStates.PENDING ||
                        this.validationState === ValidationStates.ERROR)
                ) {
                    this.reset();
                }
            },
            immediate: false,
        },
    },
})
